import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, InputGroup, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword, updateProfile,
  GoogleAuthProvider, signInWithPopup,
  sendEmailVerification
} from "firebase/auth";
import {
  doc, setDoc, getDoc, collection, query, where,
  getDocs, updateDoc, arrayUnion, increment
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, database, storage } from "../Firebase";
import inscription from '../styles/images/ticket-été.png';
import { Link } from "react-router-dom";
import { LuEye, LuEyeOff } from 'react-icons/lu';

const POINTS_VALIDATION = 250;

const CODES_PIONNIERS = {
  "HPMIGNOT":    { secteur: "Hôpital André Mignot", ville: "Le Chesnay", plafond: 50  },
  "TESTPIONNIER":{ secteur: "Tech",                  ville: "Paris",      plafond: 100 }
};

const sanitize = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.trim()
    .replace(/</g,  "&lt;")
    .replace(/>/g,  "&gt;")
    .replace(/"/g,  "&quot;")
    .replace(/'/g,  "&#x27;")
    .replace(/\//g, "&#x2F;");
};

const VALIDATIONS = {
  nom:            { regex: /^[a-zA-ZÀ-ÿ\s\-']{2,50}$/, message: "Nom invalide (2-50 caractères, lettres uniquement)" },
  prenom:         { regex: /^[a-zA-ZÀ-ÿ\s\-']{2,50}$/, message: "Prénom invalide (2-50 caractères, lettres uniquement)" },
  pseudo:         { regex: /^[a-zA-Z0-9_-]{3,20}$/,    message: "Pseudo invalide (3-20 caractères)" },
  telephone:      { regex: /^(06|07)[0-9]{8}$/,          message: "Format : 06 ou 07 suivi de 8 chiffres" },
  immatriculation:{ regex: /^[A-Za-z]{2}-[0-9]{3}-[A-Za-z]{2}$/, message: "Format attendu : AB-123-CD" },
  codeParrain:    { regex: /^[A-Z0-9]{6}$/,              message: "Code de parrainage invalide (6 caractères)" },
  password:       {
    regex: /^(?=.*\d)(?=.*[A-Z])(?=.*[!@#$%^&*\\.]).{8,}$/,
    message: "8 caractères minimum dont 1 majuscule, 1 chiffre et 1 caractère spécial"
  },
};

const validateField = (name, value) => {
  if (!VALIDATIONS[name] || !value) return null;
  return VALIDATIONS[name].regex.test(value) ? null : VALIDATIONS[name].message;
};

// ── Composant mot de passe ────────────────────────────────────────────────────
const PasswordInput = ({ label, name, value, onChange, placeholder, required, feedbackMessage, isInvalid, validatePassword }) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordValid = validatePassword
    ? value.length >= 8 && /(?=.*\d)(?=.*[A-Z])(?=.*[!@#$%^&*\\.])/.test(value)
    : true;
  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <InputGroup hasValidation>
        <Form.Control
          type={showPassword ? "text" : "password"}
          name={name} value={value} onChange={onChange}
          placeholder={placeholder} required={required} minLength={8}
          isInvalid={isInvalid || (validatePassword && value && !isPasswordValid)}
          autoComplete={name === "password" ? "new-password" : "off"}
          style={{ fontSize: '16px' }}
        />
        <InputGroup.Text
          onClick={() => setShowPassword(p => !p)}
          style={{ cursor: 'pointer', minWidth: '48px', justifyContent: 'center' }}>
          {showPassword ? <LuEyeOff size={20} /> : <LuEye size={20} />}
        </InputGroup.Text>
        <Form.Control.Feedback type="invalid">{feedbackMessage}</Form.Control.Feedback>
      </InputGroup>
      {validatePassword && (
        <Form.Text className={!isPasswordValid && value ? "text-danger" : "text-muted"}
          style={{ fontSize: '0.78rem' }}>
          8 caractères minimum (1 majuscule, 1 chiffre, 1 caractère spécial : !@#$%^&*.)
        </Form.Text>
      )}
    </Form.Group>
  );
};

// ── Modal charte ──────────────────────────────────────────────────────────────
const CharteModal = ({ show, onAccept, onDecline }) => (
  <Modal show={show} onHide={onDecline} size="lg" backdrop="static" keyboard={false} centered>
    <Modal.Header style={{ background: '#07a993', color: '#fff' }}>
      <Modal.Title style={{ fontWeight: 700, fontSize: '1rem' }}>
        📋 Charte de bonne conduite SpotyPark
      </Modal.Title>
    </Modal.Header>
    <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px' }}>
      <p style={{ color: '#555', marginBottom: '16px', fontSize: '0.88rem' }}>
        Cette charte vise à garantir une expérience fiable et respectueuse pour tous les
        utilisateurs de SpotyPark.
      </p>
      <div className="p-3 mb-3 rounded" style={{ background: '#e8f0fe', border: '1px solid #0d6efd' }}>
        <h6 style={{ fontWeight: 700, color: '#0d6efd', fontSize: '0.9rem' }}>🅿️ Règles pour les acheteurs {''} <span className='badge bg-primary' >à venir</span> </h6>
        <p style={{ fontWeight: 600, marginBottom: '6px', fontSize: '0.85rem' }}>Annulation :</p>
        <ul style={{ fontSize: '0.85rem', paddingLeft: '18px' }}>
          <li className="mb-1"><strong>Gratuite</strong> jusqu'à <strong>24h</strong> avant.</li>
          <li className="mb-1"><strong>50%</strong> entre <strong>12h et 24h</strong> avant.</li>
          <li className="mb-1"><strong>Aucun remboursement</strong> sous 12h.</li>
        </ul>
        <p style={{ fontWeight: 600, marginBottom: '6px', fontSize: '0.85rem' }}>Confirmation :</p>
        <ul style={{ fontSize: '0.85rem', paddingLeft: '18px', marginBottom: 0 }}>
          <li className="mb-1">Notification GPS à l'arrivée pour confirmer la prise de place.</li>
          <li>L'acheteur s'engage à arriver à l'heure convenue avec le cédant de la place.</li>
        </ul>
      </div>
      <div className="p-3 mb-3 rounded" style={{ background: '#e8f8f5', border: '1px solid #07a993' }}>
        <h6 style={{ fontWeight: 700, color: '#07a993', fontSize: '0.9rem' }}>🚗 Règles pour les vendeurs {''} <span className='badge bg-primary' >à venir</span></h6>
        <ul style={{ fontSize: '0.85rem', paddingLeft: '18px', marginBottom: 0 }}>
          <li className="mb-1">Attendre l'acheteur sur la place réservée.</li>
          <li className="mb-1">Notification automatique à l'heure de réservation.</li>
          <li>Non-respect → <strong>suspension de vente pendant 1 mois</strong>.</li>
        </ul>
      </div>
      <div className="p-3 mb-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
        <h6 style={{ fontWeight: 700, fontSize: '0.9rem' }}>📌 Dispositions générales</h6>
        <ul style={{ fontSize: '0.85rem', paddingLeft: '18px', marginBottom: 0 }}>
          <li className="mb-1">Respect et courtoisie dans toutes les interactions.</li>
          <li>Tout comportement inapproprié peut être signalé via l'application.</li>
        </ul>
      </div>
      <div className="p-3 rounded" style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
        <h6 style={{ fontWeight: 700, fontSize: '0.9rem' }}>🔒 Données personnelles</h6>
        <ul style={{ fontSize: '0.85rem', paddingLeft: '18px', marginBottom: 0 }}>
          <li>Géolocalisation enregistrée <strong>uniquement lors des confirmations</strong>.</li>
        </ul>
      </div>
    </Modal.Body>
    <Modal.Footer style={{ padding: '12px 16px', gap: '8px' }}>
      <Button variant="outline-secondary" onClick={onDecline} size="sm">
        Refuser
      </Button>
      <Button onClick={onAccept}
        style={{ background: '#07a993', border: 'none', fontWeight: 600 }}
        size="sm">
        ✅ J'accepte la charte
      </Button>
    </Modal.Footer>
  </Modal>
);

// ── Composant principal ───────────────────────────────────────────────────────
export default function Inscription() {
  const navigate = useNavigate();
  const [validated,           setValidated]           = useState(false);
  const [showSuccess,         setShowSuccess]         = useState(false);
  const [errorMessage,        setErrorMessage]        = useState("");
  const [isSubmitting,        setIsSubmitting]        = useState(false);
  const [fieldErrors,         setFieldErrors]         = useState({});
  const [showCharte,          setShowCharte]          = useState(false);
  const [charteAcceptee,      setCharteAcceptee]      = useState(false);
  const [inscriptionTerminee, setInscriptionTerminee] = useState(false);
  // ── Pseudo ─────────────────────────────────────────────────────────────────
  const [checkingPseudo,  setCheckingPseudo]  = useState(false);
  const [pseudoDisponible,setPseudoDisponible]= useState(null); // null | true | false

  const [formData, setFormData] = useState({
    nom: "", prenom: "", email: "", pseudo: "",
    dateNaissance: "", telephone: "", password: "", confirmPassword: "",
    typeVehicule: "", marqueVehicule: "", immatriculation: "",
    photoProfil: null, acceptConditions: false, codeParrain: "",
    codePionnier: "",
  });

  const vehiculeTypes   = ["Deux roues","Berline","Break","Citadine","SUV","Monospace","Coupé","Cabriolet","Camion","Utilitaire"];
  const marqueVehicules = ['Abarth','Acura','Alfa Romeo','Alpina','Aston Martin','Audi','Bentley','BMW','Bugatti','Buick','Cadillac','Chevrolet','Chrysler','Citroën','Dacia','Daewoo','Daihatsu','Dodge','DS','Ducati','Ferrari','Fiat','Ford','Honda','Hyundai','Infiniti','Isuzu','Jaguar','Jeep','Kia','Lada','Lamborghini','Lancia','Land Rover','Lexus','Maserati','Mazda','McLaren','Mercedes-Benz','MG','Mini','Mitsubishi','Morgan','Nissan','Opel','Peugeot','Porsche','Renault','Rover','Saab','SEAT','Skoda','Smart','SsangYong','Subaru','Suzuki','Tesla','Toyota','Vauxhall','Volkswagen','Volvo','Wuling','Zenvo'];

  const generateUniqueReferralCode = async () => {
    let code, exists;
    do {
      code = Math.random().toString(36).substring(2, 8).toUpperCase();
      const snap = await getDocs(query(collection(database, "users"), where("codeParrainage", "==", code)));
      exists = !snap.empty;
    } while (exists);
    return code;
  };

  const checkEmailUnique = async (email) => {
    const snap = await getDocs(query(collection(database, "users"), where("email", "==", email.toLowerCase().trim())));
    return snap.empty;
  };

  // ── Vérification pseudo unique ────────────────────────────────────────────
  const checkPseudoUnique = async (pseudo) => {
    const snap = await getDocs(query(
      collection(database, "users"),
      where("pseudo", "==", pseudo.trim())
    ));
    return snap.empty;
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    const newValue = type === 'checkbox' ? checked : type === 'file' ? files[0] : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (VALIDATIONS[name] && type !== 'checkbox' && type !== 'file')
      setFieldErrors(prev => ({ ...prev, [name]: validateField(name, value) }));

    // ── Vérification pseudo en temps réel ──────────────────────────────────
    if (name === 'pseudo') {
      setPseudoDisponible(null);
      if (value.length >= 3 && VALIDATIONS.pseudo.regex.test(value)) {
        setCheckingPseudo(true);
        clearTimeout(window._pseudoTimeout);
        window._pseudoTimeout = setTimeout(async () => {
          const dispo = await checkPseudoUnique(value);
          setPseudoDisponible(dispo);
          if (!dispo) setFieldErrors(prev => ({ ...prev, pseudo: "Ce pseudo est déjà pris" }));
          else setFieldErrors(prev => ({ ...prev, pseudo: null }));
          setCheckingPseudo(false);
        }, 500);
      }
    }
  };

  const validateImage = (file) => {
    if (!file) return null;
    if (!['image/jpeg','image/png','image/webp','image/gif'].includes(file.type))
      return "Format non autorisé. Utilisez JPG, PNG, WebP ou GIF.";
    if (file.size > 5 * 1024 * 1024)
      return "L'image ne doit pas dépasser 5 Mo.";
    return null;
  };

  const validateAge = (dateNaissance) => {
    if (!dateNaissance) return "Date de naissance requise";
    const birth = new Date(dateNaissance);
    const today = new Date();
    const age   = today.getFullYear() - birth.getFullYear()
      - (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
    return age < 16 ? "Vous devez avoir au moins 16 ans." : null;
  };

  const validateAll = () => {
    const errors = {};
    ['nom','prenom','pseudo','telephone','immatriculation','password'].forEach(field => {
      const err = validateField(field, formData[field]);
      if (err) errors[field] = err;
    });
    // ── Pseudo non disponible ───────────────────────────────────────────────
    if (pseudoDisponible === false) errors.pseudo = "Ce pseudo est déjà pris";

    if (formData.codeParrain) {
      const err = validateField('codeParrain', formData.codeParrain.toUpperCase());
      if (err) errors.codeParrain = err;
    }
    const ageErr = validateAge(formData.dateNaissance);
    if (ageErr) errors.dateNaissance = ageErr;
    const imgErr = validateImage(formData.photoProfil);
    if (imgErr) errors.photoProfil = imgErr;
    if (formData.password !== formData.confirmPassword)
      errors.confirmPassword = "Les mots de passe ne correspondent pas";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveUserToFirestore = async (user, userData) => {
    await setDoc(doc(database, "users", user.uid), {
      ...userData, uid: user.uid, dateCreation: new Date().toISOString()
    });
  };

  const uploadProfilePicture = async (file, userId) => {
    if (!file) return "";
    try {
      const photoRef = ref(storage, `profilePics/${userId}`);
      const snapshot = await uploadBytes(photoRef, file);
      return await getDownloadURL(snapshot.ref);
    } catch { return ""; }
  };

  const handleGoogleSignIn = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const provider = new GoogleAuthProvider();
      const result   = await signInWithPopup(auth, provider);
      const user     = result.user;
      const userSnap = await getDoc(doc(database, "users", user.uid));
      if (!userSnap.exists()) {
        const isUnique = await checkEmailUnique(user.email);
        if (!isUnique) {
          setErrorMessage("Cette adresse email est déjà associée à un compte.");
          await auth.signOut();
          return;
        }
        const code     = await generateUniqueReferralCode();
        const userData = {
          nom:    sanitize(user.displayName?.split(' ').pop() || ""),
          prenom: sanitize(user.displayName?.split(' ').shift() || ""),
          email:  user.email.toLowerCase().trim(),
          pseudo: sanitize(user.displayName || user.email?.split('@')[0] || ""),
          photoProfil: user.photoURL || "",
          abonnement: "free", abonnementActif: false, abonnementExpiration: null,
          telephone: "", emailVerifie: true,
          typeVehicule: "", marqueVehicule: "", immatriculation: "",
          points: 0, codeParrainage: code,
          parrainages: [], parrain: null,
          soldeCreditUtilisateur: 0,
          totalStars: 0, ratingsReceived: 0, averageRating: 0,
          lastRewardDate: null, ratings: [],
          charteAcceptee: false, pointsParrainageEnAttente: 0,
          estPionnier: false, numeroPionnier: null, secteurPionnier: null,
          codePionnierUtilise: null, dateInscriptionPionnier: null,
          transactionsPionnier: 0, abonnementPionnierOffert: false,
        };
        await saveUserToFirestore(user, userData);
      }
      navigate("/user");
    } catch (error) {
      if (error.code !== 'auth/popup-closed-by-user')
        setErrorMessage("Erreur lors de la connexion Google. Réessayez.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const form = e.currentTarget;
    if (!form.checkValidity() || !validateAll()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    if (!charteAcceptee) { setShowCharte(true); return; }
    await processRegistration();
  };

  const processRegistration = async () => {
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      // ── Double vérification pseudo au moment du submit ──────────────────
      const isPseudoUnique = await checkPseudoUnique(formData.pseudo);
      if (!isPseudoUnique) {
        setFieldErrors(prev => ({ ...prev, pseudo: "Ce pseudo est déjà pris" }));
        setPseudoDisponible(false);
        setIsSubmitting(false);
        return;
      }

      const isUnique = await checkEmailUnique(formData.email);
      if (!isUnique) {
        setErrorMessage("Cette adresse email est déjà associée à un compte SpotyPark.");
        setIsSubmitting(false);
        return;
      }
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user     = userCredential.user;
      const photoURL = await uploadProfilePicture(formData.photoProfil, user.uid);
      await updateProfile(user, { displayName: sanitize(formData.pseudo), photoURL });
      await sendEmailVerification(user, { url: `${window.location.origin}/login`, handleCodeInApp: false });

      let parrainId = null;
      if (formData.codeParrain) {
        try {
          const snap = await getDocs(query(collection(database, "users"), where("codeParrainage", "==", formData.codeParrain.toUpperCase())));
          if (!snap.empty) {
            const potentialId = snap.docs[0].id;
            if (potentialId !== user.uid) {
              parrainId = potentialId;
              await updateDoc(doc(database, "users", parrainId), {
                parrainages: arrayUnion(user.uid),
                pointsParrainageEnAttente: increment(POINTS_VALIDATION),
              });
            }
          }
        } catch (err) { console.warn("Erreur parrainage:", err); }
      }

      let estPionnier = false, numeroPionnier = null, secteurPionnier = null;
      const codeSaisi = formData.codePionnier?.toUpperCase().trim();
      if (codeSaisi && CODES_PIONNIERS[codeSaisi]) {
        const configCode = CODES_PIONNIERS[codeSaisi];
        const pionniersSnap = await getDocs(query(collection(database, "users"), where("codePionnierUtilise", "==", codeSaisi)));
        if (pionniersSnap.size < configCode.plafond) {
          estPionnier     = true;
          numeroPionnier  = pionniersSnap.size + 1;
          secteurPionnier = configCode.secteur;
        } else {
          setErrorMessage(`Programme pionnier complet pour ce secteur. Inscription continue normalement.`);
        }
      }

      const codeParrainage = await generateUniqueReferralCode();
      const userData = {
        nom: sanitize(formData.nom), prenom: sanitize(formData.prenom),
        email: formData.email.toLowerCase().trim(), pseudo: sanitize(formData.pseudo),
        dateNaissance: formData.dateNaissance, telephone: formData.telephone,
        typeVehicule: formData.typeVehicule, marqueVehicule: formData.marqueVehicule,
        immatriculation: formData.immatriculation.toUpperCase(), photoProfil: photoURL,
        abonnement: "free", abonnementActif: false, abonnementExpiration: null,
        points: 0, emailVerifie: false, codeParrainage, parrain: parrainId,
        parrainages: [], soldeCreditUtilisateur: 0,
        totalStars: 0, ratingsReceived: 0, averageRating: 0,
        lastRewardDate: null, ratings: [],
        charteAcceptee: true, charteAccepteeDate: new Date().toISOString(),
        pointsParrainageEnAttente: parrainId ? POINTS_VALIDATION : 0,
        estPionnier, numeroPionnier, secteurPionnier,
        codePionnierUtilise: estPionnier ? codeSaisi : null,
        dateInscriptionPionnier: estPionnier ? new Date().toISOString() : null,
        transactionsPionnier: 0, abonnementPionnierOffert: false,
      };
      await saveUserToFirestore(user, userData);
      setInscriptionTerminee(true);
    } catch (error) {
      if (error.code === 'auth/email-already-in-use')
        setErrorMessage("Cette adresse email est déjà utilisée.");
      else {
        setErrorMessage("Une erreur est survenue. Veuillez réessayer.");
        console.error("Erreur inscription:", error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptCharte = async () => {
    setShowCharte(false);
    setCharteAcceptee(true);
    await processRegistration();
  };

  const handleDeclineCharte = () => {
    setShowCharte(false);
    setErrorMessage("Vous devez accepter la charte de bonne conduite pour vous inscrire.");
  };

  // ── Page confirmation email ───────────────────────────────────────────────
  if (inscriptionTerminee) {
    return (
      <Container className="py-4 px-3" style={{ maxWidth: '480px' }}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4 text-center">
            <div style={{ fontSize: '3rem' }}>📧</div>
            <h3 style={{ fontWeight: 700, color: '#07a993', fontSize: '1.4rem' }} className="mt-2">
              Vérifiez votre email !
            </h3>
            <p className="text-muted mt-3" style={{ fontSize: '0.9rem' }}>
              Un email de vérification a été envoyé à{' '}
              <strong>{formData.email}</strong>.
            </p>
            <Alert variant="warning" style={{ fontSize: '0.85rem', textAlign: 'left' }}>
              ⚠️ Cliquez sur le lien dans l'email pour activer votre compte.
            </Alert>
            <p className="text-muted" style={{ fontSize: '0.82rem' }}>
              Vérifiez également vos spams.
            </p>
            <Button
              style={{ background: '#07a993', border: 'none', fontWeight: 600 }}
              className="w-100 mt-2"
              onClick={() => navigate("/login")}>
              Aller à la page de connexion
            </Button>
            <Button variant="link"
              style={{ fontSize: '0.85rem', color: '#6c757d' }}
              className="mt-2"
              onClick={async () => {
                try {
                  await sendEmailVerification(auth.currentUser);
                  alert("Email renvoyé !");
                } catch {
                  alert("Erreur lors du renvoi. Réessayez dans quelques minutes.");
                }
              }}>
              Renvoyer l'email de vérification
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0">
      <CharteModal show={showCharte} onAccept={handleAcceptCharte} onDecline={handleDeclineCharte} />

      {/* ── Image hero ────────────────────────────────────────────────────── */}
      <Row className="g-0 d-none d-md-flex">
        <Col className="d-flex justify-content-center">
          <img src={inscription} alt="Inscription"
            className="img-fluid object-fit-cover"
            style={{ height: '40vh', width: "80%", objectFit: 'cover' }} />
        </Col>
      </Row>

      {/* ── Formulaire ────────────────────────────────────────────────────── */}
      <Row className="g-0 justify-content-center">
        <Col xs={12} md={10} lg={7} className="px-3 px-md-4 py-4">
          <Card className="border-0">
            <Card.Body className="p-0 p-md-3">

              <h2 className="text-center mb-4" style={{ fontSize: '1.5rem', fontWeight: 700 }}>
                Inscription
              </h2>

              {showSuccess   && <Alert variant="success" className="mb-3">Inscription réussie !</Alert>}
              {errorMessage  && <Alert variant="danger"  className="mb-3" style={{ fontSize: '0.88rem' }}>{errorMessage}</Alert>}

              {/* Bouton Google */}
              <Button variant="outline-dark" className="w-100 mb-3"
                onClick={handleGoogleSignIn} disabled={isSubmitting}
                style={{ padding: '12px', fontSize: '0.95rem' }}>
                <i className="bi bi-google me-2"></i>Continuer avec Google
              </Button>

              <div className="text-center mb-3" style={{ position: 'relative' }}>
                <hr />
                <span style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: '#fff', padding: '0 12px',
                  color: '#6c757d', fontSize: '0.85rem',
                }}>
                  ou
                </span>
              </div>

              <Form noValidate validated={validated} onSubmit={handleSubmit}>

                {/* Nom + Prénom */}
                <Row className="g-2 mb-1">
                  <Col xs={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontSize: '0.88rem', fontWeight: 600 }}>Nom</Form.Label>
                      <Form.Control
                        type="text" name="nom" value={formData.nom}
                        onChange={handleChange} required isInvalid={!!fieldErrors.nom}
                        pattern="[a-zA-ZÀ-ÿ\s\-']{2,50}"
                        style={{ fontSize: '16px' }}
                      />
                      <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                        {fieldErrors.nom || "Nom invalide"}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontSize: '0.88rem', fontWeight: 600 }}>Prénom</Form.Label>
                      <Form.Control
                        type="text" name="prenom" value={formData.prenom}
                        onChange={handleChange} required isInvalid={!!fieldErrors.prenom}
                        pattern="[a-zA-ZÀ-ÿ\s\-']{2,50}"
                        style={{ fontSize: '16px' }}
                      />
                      <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                        {fieldErrors.prenom || "Prénom invalide"}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Email */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.88rem', fontWeight: 600 }}>Email</Form.Label>
                  <Form.Control
                    type="email" name="email" value={formData.email}
                    onChange={handleChange} required autoComplete="email"
                    inputMode="email"
                    style={{ fontSize: '16px' }}
                  />
                  <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                    Email invalide
                  </Form.Control.Feedback>
                </Form.Group>

                {/* ── Pseudo avec vérification temps réel ──────────────────── */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.88rem', fontWeight: 600 }}>Pseudo</Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control
                      type="text" name="pseudo" value={formData.pseudo}
                      onChange={handleChange} required
                      isInvalid={!!fieldErrors.pseudo}
                      isValid={pseudoDisponible === true && !fieldErrors.pseudo}
                      pattern="[a-zA-Z0-9_\-]{3,20}"
                      style={{ fontSize: '16px', paddingRight: '36px' }}
                    />
                    {/* Indicateur temps réel */}
                    {checkingPseudo && (
                      <div style={{
                        position: 'absolute', right: '12px', top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '0.75rem', color: '#6c757d',
                      }}>⏳</div>
                    )}
                    {!checkingPseudo && pseudoDisponible === true && (
                      <div style={{
                        position: 'absolute', right: '12px', top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '0.88rem', color: '#198754',
                      }}>✅</div>
                    )}
                    {!checkingPseudo && pseudoDisponible === false && (
                      <div style={{
                        position: 'absolute', right: '12px', top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '0.88rem', color: '#dc3545',
                      }}>❌</div>
                    )}
                  </div>
                  <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                    {fieldErrors.pseudo || "3-20 caractères"}
                  </Form.Control.Feedback>
                  {checkingPseudo && (
                    <Form.Text style={{ color: '#6c757d', fontSize: '0.75rem' }}>
                      ⏳ Vérification en cours...
                    </Form.Text>
                  )}
                  {!checkingPseudo && pseudoDisponible === true && (
                    <Form.Text style={{ color: '#198754', fontSize: '0.75rem' }}>
                      ✅ Pseudo disponible !
                    </Form.Text>
                  )}
                  {!checkingPseudo && pseudoDisponible === false && (
                    <Form.Text style={{ color: '#dc3545', fontSize: '0.75rem' }}>
                      ❌ Ce pseudo est déjà pris, choisissez-en un autre.
                    </Form.Text>
                  )}
                </Form.Group>

                {/* Date de naissance */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.88rem', fontWeight: 600 }}>Date de naissance</Form.Label>
                  <Form.Control
                    type="date" name="dateNaissance" value={formData.dateNaissance}
                    onChange={handleChange} required isInvalid={!!fieldErrors.dateNaissance}
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 16)).toISOString().split('T')[0]}
                    style={{ fontSize: '16px' }}
                  />
                  <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                    {fieldErrors.dateNaissance || "16 ans minimum"}
                  </Form.Control.Feedback>
                  <Form.Text muted style={{ fontSize: '0.75rem' }}>16 ans minimum</Form.Text>
                </Form.Group>

                {/* Téléphone */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.88rem', fontWeight: 600 }}>Téléphone</Form.Label>
                  <Form.Control
                    type="tel" name="telephone" value={formData.telephone}
                    onChange={handleChange} required isInvalid={!!fieldErrors.telephone}
                    pattern="(06|07)[0-9]{8}" inputMode="numeric" maxLength={10}
                    placeholder="06XXXXXXXX"
                    style={{ fontSize: '16px' }}
                  />
                  <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                    {fieldErrors.telephone || "Format 06 ou 07 + 8 chiffres"}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Mots de passe */}
                <PasswordInput
                  label="Mot de passe" name="password"
                  value={formData.password} onChange={handleChange}
                  placeholder="••••••••" required
                  feedbackMessage={fieldErrors.password || VALIDATIONS.password.message}
                  isInvalid={!!fieldErrors.password} validatePassword={true}
                />
                <PasswordInput
                  label="Confirmer le mot de passe" name="confirmPassword"
                  value={formData.confirmPassword} onChange={handleChange}
                  placeholder="••••••••" required
                  feedbackMessage={fieldErrors.confirmPassword || "Les mots de passe ne correspondent pas"}
                  isInvalid={!!fieldErrors.confirmPassword || (validated && formData.password !== formData.confirmPassword)}
                />

                {/* Type de véhicule */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.88rem', fontWeight: 600 }}>Type de véhicule</Form.Label>
                  <Form.Select
                    name="typeVehicule" value={formData.typeVehicule}
                    onChange={handleChange} required
                    style={{ fontSize: '16px' }}>
                    <option value="">Sélectionnez un type</option>
                    {vehiculeTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </Form.Select>
                </Form.Group>

                {/* Marque du véhicule */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.88rem', fontWeight: 600 }}>Marque du véhicule</Form.Label>
                  <Form.Select
                    name="marqueVehicule" value={formData.marqueVehicule}
                    onChange={handleChange} required
                    style={{ fontSize: '16px' }}>
                    <option value="">Sélectionnez une marque</option>
                    {marqueVehicules.map(m => <option key={m} value={m}>{m}</option>)}
                  </Form.Select>
                </Form.Group>

                {/* Immatriculation */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.88rem', fontWeight: 600 }}>Immatriculation</Form.Label>
                  <Form.Control
                    type="text" name="immatriculation"
                    value={formData.immatriculation} onChange={handleChange}
                    required isInvalid={!!fieldErrors.immatriculation}
                    pattern="[A-Za-z]{2}-[0-9]{3}-[A-Za-z]{2}"
                    maxLength={9} placeholder="AB-123-CD"
                    style={{ fontSize: '16px', textTransform: 'uppercase' }}
                  />
                  <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                    {fieldErrors.immatriculation || "Format : AB-123-CD"}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Photo de profil */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.88rem', fontWeight: 600 }}>
                    Photo de profil{' '}
                    <span className="text-muted fw-normal">(optionnel)</span>
                  </Form.Label>
                  <Form.Control
                    type="file" name="photoProfil" onChange={handleChange}
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    isInvalid={!!fieldErrors.photoProfil}
                    style={{ fontSize: '14px' }}
                  />
                  <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                    {fieldErrors.photoProfil}
                  </Form.Control.Feedback>
                  <Form.Text muted style={{ fontSize: '0.75rem' }}>
                    JPG, PNG, WebP ou GIF — 5 Mo max
                  </Form.Text>
                </Form.Group>

                {/* ── Codes optionnels ──────────────────────────────────────── */}
                <div className="p-3 rounded mb-3" style={{
                  background: '#f8f9fa', border: '1px solid #dee2e6'
                }}>
                  <p style={{ fontSize: '0.82rem', color: '#6c757d', marginBottom: '12px', fontWeight: 600 }}>
                    Codes optionnels
                  </p>

                  {/* Code parrainage */}
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      Code de parrainage
                    </Form.Label>
                    <Form.Control
                      type="text" name="codeParrain"
                      value={formData.codeParrain} onChange={handleChange}
                      placeholder="Ex: ABC123" maxLength={6}
                      isInvalid={!!fieldErrors.codeParrain}
                      style={{ fontSize: '16px', textTransform: 'uppercase' }}
                    />
                    <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                      {fieldErrors.codeParrain}
                    </Form.Control.Feedback>
                    <Form.Text muted style={{ fontSize: '0.75rem' }}>
                      Recevez <strong>{POINTS_VALIDATION} points</strong> après vérification de votre email.
                    </Form.Text>
                  </Form.Group>

                  {/* Code pionnier */}
                  <Form.Group className="mb-0">
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      Codes spéciaux
                    </Form.Label>
                    <Form.Control
                      type="text" name="codePionnier"
                      value={formData.codePionnier} onChange={handleChange}
                      placeholder="Code fourni par SpotyPark"
                      maxLength={20}
                      style={{ fontSize: '16px', textTransform: 'uppercase' }}
                    />
                    <Form.Text muted style={{ fontSize: '0.75rem' }}>
                      Accès exclusif aux fonctionnalités en avant-première.
                    </Form.Text>
                  </Form.Group>
                </div>

                {/* Conditions */}
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox" name="acceptConditions"
                    checked={formData.acceptConditions} onChange={handleChange} required
                    label={
                      <span style={{ fontSize: '0.88rem' }}>
                        J'accepte les{" "}
                        <Link to="/confidentialite" target="_blank">conditions d'utilisation</Link>
                      </span>
                    }
                  />
                </Form.Group>

                <Alert variant="info" style={{ fontSize: '0.82rem', padding: '10px 14px' }}>
                  📋 Une charte de bonne conduite vous sera présentée avant de finaliser.
                </Alert>

                {/* Bouton submit */}
                <Button
                  type="submit" variant="primary" className="w-100"
                  disabled={isSubmitting || checkingPseudo || pseudoDisponible === false}
                  style={{ padding: '14px', fontSize: '1rem', fontWeight: 700 }}>
                  {isSubmitting    ? "Inscription en cours..." :
                   checkingPseudo  ? "Vérification du pseudo..." :
                   "S'inscrire"}
                </Button>
              </Form>

              <div className="text-center mt-4" style={{ fontSize: '0.9rem' }}>
                Déjà inscrit ?{' '}
                <Link to="/login" style={{ fontWeight: 600, color: '#07a993' }}>
                  Connectez-vous
                </Link>
              </div>

            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}